import { useEffect, useState } from "react";
import { useNavigate, NavLink, Outlet, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LogOut, Package, MapPin, Heart, User } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

export default function AccountLayout() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("jow_token");
    if (!token) {
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then((r) => setMe(r.data))
      .catch(() => {
        localStorage.removeItem("jow_token");
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center label-overline text-jow-navy/55" data-testid="account-loading">
        Loading…
      </div>
    );
  }

  if (!me) return <Navigate to="/login" replace />;

  const signOut = () => {
    localStorage.removeItem("jow_token");
    toast.success("Signed out.");
    navigate("/");
  };

  const tabs = [
    { to: "/account", label: "Profile", end: true, Icon: User, testid: "tab-profile" },
    { to: "/account/orders", label: "Orders", Icon: Package, testid: "tab-orders" },
    { to: "/account/addresses", label: "Addresses", Icon: MapPin, testid: "tab-addresses" },
    { to: "/wishlist", label: "Wishlist", Icon: Heart, testid: "tab-wishlist" },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16" data-testid="account-page">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <span className="label-overline text-jow-gold">Welcome</span>
        <h1 className="font-display text-4xl md:text-5xl mt-2 text-jow-navy">
          {me.name || me.phone}
        </h1>
        <p className="text-sm text-jow-navy/55 mt-1" data-testid="account-phone">{me.phone}</p>
      </motion.div>

      <div className="mt-10 grid lg:grid-cols-12 gap-10">
        <aside className="lg:col-span-3" data-testid="account-sidebar">
          <ul className="bg-white border border-jow-navy/10 divide-y divide-jow-navy/5">
            {tabs.map((t) => (
              <li key={t.to}>
                <NavLink
                  to={t.to}
                  end={t.end}
                  data-testid={t.testid}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-5 py-4 text-sm transition ${
                      isActive ? "bg-jow-cream text-jow-pink" : "text-jow-navy hover:text-jow-pink"
                    }`
                  }
                >
                  <t.Icon size={15} /> {t.label}
                </NavLink>
              </li>
            ))}
            <li>
              <button
                onClick={signOut}
                className="w-full text-left flex items-center gap-3 px-5 py-4 text-sm text-jow-navy/70 hover:text-jow-pink"
                data-testid="account-signout"
              >
                <LogOut size={15} /> Sign out
              </button>
            </li>
          </ul>
        </aside>

        <main className="lg:col-span-9">
          <Outlet context={{ me, refresh: () => api.get("/auth/me").then((r) => setMe(r.data)) }} />
        </main>
      </div>
    </div>
  );
}
